export default class HttpError extends Error {
    /**
     * @type {{request:*, sessionExpired: boolean}}
     */
    #reason;
    /**
     * @type {SecurityContext}
     */
    #securityContext;

    /**
     * @param {{request:*, sessionExpired: boolean}} reason
     * @param {SecurityContext} securityContext
     */
    constructor(reason, securityContext) {
        super(`Http error ${reason.sessionExpired ? 'session expired' : reason.request.status}.`);
        this.#reason = reason;
        this.#securityContext = securityContext;
    }
    /**
     * @returns {number}
     */
    get status() {
        return this.#reason.request.status;
    }

    /** @returns {string} */
    get responseType(){
        return this.#reason.request.responseType
    }
    /**
     * @returns {*}
     */
    get response() {
        return this.#reason.request.response;
    }
    /**
     * Error bodies are supposed to be JSON (see RestExceptionHandler), but not every failure comes from there:
     * Tomcat/proxy errors, aborted requests and non-JSON endpoints can return HTML, plain text or nothing. Such a
     * body must not blow up the error handling itself, otherwise the user sees no error dialog at all.
     *
     * @returns {*} always an object, empty if the body couldn't be parsed
     */
    get responseJson(){
        const response = this.response;
        // Binary endpoints (blob, detector-run) use responseType 'arraybuffer'; the error body is still JSON, so
        // decode it - otherwise reading e.g. `.code` off the raw ArrayBuffer silently yields undefined.
        if (response instanceof ArrayBuffer)
            return HttpError.#parseJson(new TextDecoder().decode(response));
        if (this.responseType === "text")
            return HttpError.#parseJson(response);
        return response instanceof Object ? response : {};
    }
    static #parseJson(text){
        try {
            return JSON.parse(text);
        } catch (e) {
            return {};
        }
    }
    /** @returns {string} */
    get errorMessage(){
        return this.responseJson.errorMessage;
    }
    /**
     * @returns {SecurityContext}
     */
    get securityContext() {
        return this.#securityContext;
    }
    /**
     * @returns {boolean}
     */
    get isBadRequest() {
        return this.status === 400;
    }
    /**
     * @returns {boolean}
     */
    get isUnauthorized() {
        return this.status === 401;
    }
    /**
     * @returns {boolean}
     */
    get isForbidden() {
        return this.status === 403;
    }
    /**
     * @returns {boolean}
     */
    get isNotFound() {
        return this.status === 404;
    }
    /**
     * @returns {boolean}
     */
    get isInternalServerError() {
        return this.status === 500;
    }
    /**
     * @returns {boolean}
     */
    get isTooManyRequestsError() {
        return this.status === 429;
    }

    /**
     * The app is unusable because no valid license is currently in effect (see LicenseGateFilter).
     * @returns {boolean}
     */
    get isLicenseRequired() {
        return this.status === 402;
    }

    /**
     * @returns {boolean}
     */
    get isSeatsExceeded() {
        return this.isLicenseRequired && this.responseJson.code === "SEATS_EXCEEDED";
    }

    /**
     * @returns {boolean}
     */
    get isUserSessionExpired() {
        return this.#reason.sessionExpired;
    }

    /**
     * @return {boolean}
     */
    get isBillingQuotaReached(){
        return this.isOrgSpaceQuotaReached || this.isOrgRequestsQuotaReached;
    }

    /**
     * @returns {boolean}
     */
    get isOrgSpaceQuotaReached(){
        return this.isForbidden && this.responseJson.code === "ORG_SPACE_QUOTA_REACHED"
    }
    get isOrgRequestsQuotaReached(){
        return this.isForbidden && this.responseJson.code === "ORG_REQUESTS_QUOTA_REACHED"
    }
}
